/**
 * Chuck Widget - Embeddable Webchat Widget
 *
 * Usage:
 * <script src="https://your-domain.com/widget.umd.js"></script>
 * <script>
 *   ChuckWidget.init({
 *     projectId: 'your-project-id',
 *     apiUrl: 'https://your-api-url.com',
 *   });
 * </script>
 */

import { render } from 'preact';
import { Widget } from './components/Widget';
import { injectStyles, WIDGET_ID } from './styles';
import * as store from './store';
import * as api from './api';
import type { WidgetConfig } from './types';
import { emitWidgetAnalytics } from './analytics';

let isInitialized = false;
let shadowRoot: ShadowRoot | null = null;
let widgetRoot: HTMLDivElement | null = null;

/**
 * Initialize the widget
 */
function init(config: WidgetConfig): void {
  if (isInitialized) {
    console.warn('ChuckWidget is already initialized');
    return;
  }

  if (!config.projectId) {
    throw new Error('ChuckWidget: projectId is required');
  }

  // Create container
  const container = document.createElement('div');
  container.id = WIDGET_ID;
  document.body.appendChild(container);

  // Create shadow DOM for style isolation
  shadowRoot = container.attachShadow({ mode: 'open' });

  // Inject styles
  const style = document.createElement('style');
  style.textContent = injectStyles(
    config.headerColor || '#ff6801',
    config.bodyColor || '#ffffff',
    config.textColor || '#1f2937'
  );
  shadowRoot.appendChild(style);

  // Create widget root
  widgetRoot = document.createElement('div');
  shadowRoot.appendChild(widgetRoot);

  // Configure synchronously so immediate/programmatic opens cannot race a Preact effect.
  api.configure(config.apiUrl || '', config.projectId);

  // Render widget
  render(<Widget config={config} />, widgetRoot);

  isInitialized = true;

  emitWidgetAnalytics('chuck_widget_ready', {
    project_id: config.projectId,
  });

  // Call onReady callback
  if (config.onReady) {
    config.onReady();
  }
}

/**
 * Destroy the widget
 */
function destroy(): void {
  if (!isInitialized) {
    return;
  }

  const container = document.getElementById(WIDGET_ID);
  if (widgetRoot) {
    render(null, widgetRoot);
  }
  if (container) {
    container.remove();
  }

  api.resetRuntime();

  isInitialized = false;
  shadowRoot = null;
  widgetRoot = null;
}

/**
 * Open the widget programmatically
 */
function open(): void {
  if (!isInitialized) {
    console.warn('ChuckWidget is not initialized');
    return;
  }

  store.openWidget();
}

/**
 * Close the widget programmatically
 */
function close(): void {
  if (!isInitialized) {
    console.warn('ChuckWidget is not initialized');
    return;
  }

  store.closeWidget();
}

/**
 * Toggle the widget programmatically
 */
function toggle(): void {
  if (!isInitialized) {
    console.warn('ChuckWidget is not initialized');
    return;
  }

  store.toggleWidget();
}

// Export the API
const ChuckWidget = {
  init,
  destroy,
  open,
  close,
  toggle,
};

export { ChuckWidget, init, destroy, open, close, toggle };
export type { WidgetConfig };

// Auto-expose to window for UMD builds
if (typeof window !== 'undefined') {
  (window as any).ChuckWidget = ChuckWidget;
}
